/**
 * 실시간(Colyseus) 메시지 프로토콜 — docs/build/02 §5 (M2).
 * 클라/서버 단일 정의. 룸 타입·메시지 채널·페이로드.
 */
import type { Dir, Motion, HostAction } from "./index";
import { MAX_NICK } from "./hero";

export const ROOM_ZONE = "zone";
export const ZONE_MAX_CLIENTS = 40; // 초과 시 새 인스턴스
export const EVENT_MAX_CLIENTS = 100; // 이벤트존
export const MOVE_HZ = 10; // 좌표 송신 10틱/s

// ── 서버 입력 검증(적대적 WS 입력 방어) — 클라/서버 공유 순수 함수 ──
export const ZONES = ["lobby", "book", "gyeongju", "goods", "event"] as const;
export type ZoneName = (typeof ZONES)[number];
const DIRS: readonly Dir[] = ["up", "down", "left", "right"];
const MOTIONS: readonly Motion[] = ["idle", "walk", "emote"];
/**
 * 🔴 **같은 수를 두 이름으로 갖고 있지 않는다.** 별명 길이 상한은 `MAX_NICK` 하나다 —
 * 여기 16을 또 적어 두면 한쪽만 바뀔 때 아무 소리 없이 갈린다(실시간은 20자를 받고
 * 화면은 16자로 자르는 식으로). 이름은 프로토콜 쪽 말이라 남긴다.
 */
export const MAX_NICKNAME_LEN = MAX_NICK;
export const MAX_PARTS_LEN = 64;
export const MAX_CHAT_LEN = 200;

/** zone 화이트리스트 — 미지/비문자열은 lobby 로(무한 룸 생성 차단). */
export function normalizeZone(z: unknown): ZoneName {
  return typeof z === "string" && (ZONES as readonly string[]).includes(z)
    ? (z as ZoneName)
    : "lobby";
}
/** dir 화이트리스트 — 무효값은 null(상태 브로드캐스트 오염 차단). */
export function safeDir(d: unknown): Dir | null {
  return typeof d === "string" && (DIRS as readonly string[]).includes(d) ? (d as Dir) : null;
}
/** motion 화이트리스트 — 무효값은 null. */
export function safeMotion(m: unknown): Motion | null {
  return typeof m === "string" && (MOTIONS as readonly string[]).includes(m)
    ? (m as Motion)
    : null;
}

// 메시지 채널명(C→S, S→C)
export const MSG = {
  move: "move", // C→S {x,y,dir,motion}
  chat: "chat", // C→S {text} / S→C {nickname,text,ts}
  system: "system", // S→C {kind,msg}
  host: "host", // C→S(admin) {action,target?}
} as const;

// 룸 입장 옵션
export interface ZoneJoinOptions {
  zone: string; // lobby|book|gyeongju|goods|event
  nickname?: string; // 미입력 시 서버가 자동 표시명 부여
  parts: string; // 아바타 파츠 "b2_h1_a3"
  base?: string; // 히어로 프리셋(explorer_a~e|custom) — 원격 아바타를 실제 외형으로 렌더하려면 필요.
  palette?: string;
  adminToken?: string; // 진행자 인가(onAuth 1회 검증, S2-7)
}

// C→S
export interface MoveMsg {
  x: number;
  y: number;
  dir: Dir;
  motion: Motion;
}
export interface ChatInMsg {
  text: string;
}
export interface HostMsg {
  action: HostAction;
  target?: string; // 대상 sessionId
}

// S→C
export interface ChatOutMsg {
  nickname: string;
  text: string;
  ts: number;
}
export type SystemKind = "notice" | "muted" | "kicked" | "host_granted";
export interface SystemMsg {
  kind: SystemKind;
  msg: string;
}
